import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerInputEvent,
} from '@angular/material/datepicker';
import { PayeeIconButtonComponent } from '../../payee-buttons/payee-icon-button/payee-icon-button.component';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';
import { PayeeInputDirective } from '../../payee-input/payee-input.directive';

@Component({
  selector: 'payee-datepicker',
  templateUrl: './payee-datepicker.component.html',
  styleUrls: ['./payee-datepicker.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PayeeDatepickerComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PayeeDatepickerComponent),
      multi: true,
    },
  ],
  imports: [
    PayeeInputDirective,
    MatDatepickerInput,
    FormsModule,
    ReactiveFormsModule,
    PayeeIconButtonComponent,
    PayeeIconComponent,
    MatDatepicker,
  ],
})
export class PayeeDatepickerComponent implements ControlValueAccessor, Validator, OnChanges {
  @Input() placeholder = '';
  @Input() min: Date | string | null = null;
  @Input() max: Date | string | null = null;

  value: Date | string | null = null;
  disabled = false;

  @ViewChild(MatDatepicker) picker!: MatDatepicker<string | Date | null>;

  open(): void {
    if (!this.disabled) {
      this.picker.open();
    }
  }

  // ControlValueAccessor

  onChange = (_obj: any) => { };

  onTouched = () => { };

  private onValidatorChange = (): void => {};

  ngOnChanges(): void {
    this.onValidatorChange();
  }

  registerOnChange(fn: (obj: any) => void): void {
    this.onChange = fn;
  };

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  };

  writeValue(obj: any): void {
    this.value = obj;
  };

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onDateChange(event: MatDatepickerInputEvent<string | Date | null>) {
    this.value = event.value;
    this.onChange(event.value);
    this.onTouched();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const date = this.calendarDay(control.value);
    if (date === null) {
      return null;
    }

    const min = this.calendarDay(this.min);
    if (min !== null && date < min) {
      return { matDatepickerMin: { min: this.min, actual: control.value } };
    }

    const max = this.calendarDay(this.max);
    if (max !== null && date > max) {
      return { matDatepickerMax: { max: this.max, actual: control.value } };
    }

    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  private calendarDay(value: unknown): number | null {
    const date = this.toDate(value);
    if (!date) {
      return null;
    }

    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  }

  private toDate(value: unknown): Date | null {
    if (value == null || value === '') {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (match) {
      const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      return Number.isNaN(date.getTime()) ? null : date;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
}
